import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Chapter from "./Chapter";
import { IDS } from "@/lib/ids";

const FAQ = [
  {
    id: "accuracy",
    q: "Is the crosswalk actually accurate — or is it AI-guessed mapping?",
    a: "The crosswalk is mapped at the standard-clause level by a compliance team, then reviewed by security & privacy leads. Every mapping is human-reviewable in-app: you can see exactly which ISO clause is linked to which SOC 2 criterion, which HIPAA regulation, and why. Nothing is auto-inferred from a language model.",
  },
  {
    id: "dpdp",
    q: "How well does NormShift really handle India's DPDP Act?",
    a: "DPDP is a first-class framework, not a GDPR reskin. Notice under §4, Consent Manager handshake, cross-border transfer classification, data principal rights request handling, and the 72-hour breach notice — all modelled natively. The DPIA module speaks the DPDP language, not just GDPR's.",
  },
  {
    id: "audit-trust",
    q: "Why should an auditor trust your activity log?",
    a: "Because they don't have to trust us. Every event is written into a hash-chained ledger — each block's hash is derived from the previous block's contents. Any tampering breaks the chain and downstream verification fails. Auditors can export the chain and independently verify it end-to-end.",
  },
  {
    id: "iso27001-free",
    q: "What's the catch on ISO 27001 being free?",
    a: "None. ISO 27001 is genuinely free — no time-lock, no collaborator cap, no watermarked reports. We're pre-launch, onboarding design partners, and we'd rather earn your trust on ISO 27001 than gate it. When you're ready for SOC 2 or GDPR or DPDP or ISO 42001, you'll pay for those.",
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState("accuracy");

  return (
    <section className="relative py-14 md:py-20 hairline-t">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Chapter number="10" label="Objections · answered" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="font-display text-[32px] md:text-[44px] leading-[1.04] tracking-[-0.022em] text-balance"
            >
              You should be <span className="grad-text">skeptical</span>. Here's the plain answer.
            </motion.h2>
          </div>

          <div className="lg:col-span-8">
            <div className="hairline-t hairline-b">
              {FAQ.map((item) => {
                const isOpen = open === item.id;
                return (
                  <div key={item.id} className="border-b border-white/6 last:border-b-0">
                    <button
                      data-testid={IDS.faqItem(item.id)}
                      onClick={() => setOpen(isOpen ? null : item.id)}
                      className="w-full text-left py-6 grid grid-cols-12 items-start gap-4 group"
                      aria-expanded={isOpen}
                    >
                      <span className="hidden md:block col-span-1 font-mono text-[10px] tracking-[0.22em] text-ink-muted mt-1">{item.id.toUpperCase().slice(0,3)}</span>
                      <span className="col-span-11 md:col-span-10 font-display text-[18px] md:text-[22px] tracking-[-0.01em] leading-snug">{item.q}</span>
                      <span className={`col-span-1 justify-self-end font-mono text-[16px] transition-transform duration-300 ${isOpen ? "rotate-45 text-brand-red" : "text-white/50"}`}>+</span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.22,1,0.36,1] }}
                          className="overflow-hidden"
                        >
                          <div className="pb-6 pl-[8.33%] pr-[8.33%]">
                            <p className="text-ink-muted text-[14.5px] leading-relaxed max-w-[68ch]">{item.a}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
